// BlastAnimationTest.jsx
//
// UNLISTED prototype screen — not linked from any nav. Lives at /blast-test.
// Purpose: dial in the "hero acts -> villain reacts -> hero decides" reveal
// animation in isolation, using hardcoded mock data (see mockScenarios.js),
// before touching the real BlastTable / hand-generator pipeline at all.
//
// Reuses the exact same visual chrome as the real Blast question screen
// (BlastTable.css's .blast-table/.blast-felt/.blast-table-wrapper classes,
// plus the shared Seat/HeroSeat/QuestionText/ButtonGroup components) so the
// only genuinely new things being prototyped are the step-sequence timing
// and the transition CSS — not a new look.
import React, { useState, useCallback } from 'react';
import BlastSeat from '../../components/tables/blast/BlastSeat/BlastSeat';
import BlastHeroSeat from '../../components/tables/blast/BlastHeroSeat/BlastHeroSeat';
import QuestionText from '../../components/layout/BottomControls/Components/QuestionText/QuestionText';
import ButtonGroup from '../../components/common/buttons/ButtonGroup';
import { ButtonLarge } from '../../components/common/buttons/ButtonLarge/ButtonLarge';
import useHandActionSequence from '../../components/tables/blast/HandActionSequence/useHandActionSequence';
import SB_OPEN_VS_BB_3BET_SMALL from '../../components/tables/blast/HandActionSequence/mockScenarios';

// Reuse the real Blast table chrome — no new felt/seat CSS invented here.
import '../../components/tables/blast/BlastTable.css';
import './blast-animation-test.css';

const scenario = SB_OPEN_VS_BB_3BET_SMALL;

const BlastAnimationTest = () => {
  const { steps, startingStack, rangeTitle, startingBets } = scenario;
  const { currentStep, isDecision, seatState, triggerLockedAction, reset } =
    useHandActionSequence(steps, startingBets);

  const [chosenAnswer, setChosenAnswer] = useState(null);

  // Brief "chip pulse" flag so the bet amount that just changed gets a beat
  // of visual emphasis (CSS keyframe in blast-animation-test.css), instead of
  // just snapping to the new number.
  const [pulseActor, setPulseActor] = useState(null);
  // Seed with the real starting blinds, not 0 — otherwise the first render
  // (heroBet 0.5, villainBet 1) reads as "changed since mount" and fires a
  // pulse before any real action has happened.
  const prevBetsRef = React.useRef({ heroBet: startingBets.hero, villainBet: startingBets.villain });
  React.useEffect(() => {
    const prev = prevBetsRef.current;
    if (seatState.heroBet !== prev.heroBet) setPulseActor('hero');
    else if (seatState.villainBet !== prev.villainBet) setPulseActor('villain');
    prevBetsRef.current = { heroBet: seatState.heroBet, villainBet: seatState.villainBet };
    if (seatState.heroBet !== prev.heroBet || seatState.villainBet !== prev.villainBet) {
      const t = setTimeout(() => setPulseActor(null), 500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [seatState.heroBet, seatState.villainBet]);

  const handleDecision = useCallback((label) => {
    setChosenAnswer(label);
  }, []);

  const handleReplay = useCallback(() => {
    setChosenAnswer(null);
    // Also re-seed the pulse-detection ref back to the starting blinds —
    // otherwise the first render after reset (back to 0.5/1) reads as a
    // "change" from the previous run's end state (3/6) and fires a bogus
    // pulse immediately on replay.
    prevBetsRef.current = { heroBet: startingBets.hero, villainBet: startingBets.villain };
    setPulseActor(null);
    reset();
  }, [reset, startingBets]);

  const villainRemaining = (startingStack - seatState.villainBet).toFixed(1);
  const heroRemaining = startingStack.toFixed(1); // HeroSeat computes remaining internally from blinds/bet

  const questionText =
    currentStep?.type === 'lockedAction'
      ? currentStep.questionText
      : currentStep?.type === 'decision'
      ? currentStep.question
      : ''; // reveal steps show no question — the reveal itself is the message

  return (
    <div className="blast-table blast-anim-test">
      <div className="blast-header-block">
        <div className="blast-header-nav-row">
          <span className="blast-anim-test-badge">TEST — /blast-test</span>
        </div>
        <h1 className="blast-header-title">Blast - Heads Up</h1>

        {/* Mocked, not wired to a real quiz session — this prototype only
            ever runs one scenario. Included for visual parity with the real
            BlastTable header; reuses its exact .blast-progress-* classes
            from BlastTable.css, no new CSS. Fixed at "1/10" since there's
            nothing real to count yet. */}
        <div className="blast-progress">
          <div className="blast-progress-bar">
            <div className="blast-progress-fill" style={{ width: '10%' }} />
          </div>
          <div className="blast-hand-counter">1/10</div>
        </div>
      </div>

      <div className="blast-table-wrapper">
        <div className="blast-felt" />
        <div className="range-title">{rangeTitle}</div>

        <BlastSeat
          className={`seat-3 blast-anim-test-seat ${pulseActor === 'villain' ? 'blast-anim-test-pulse' : ''}`}
          title={scenario.villainPosition}
          blinds={villainRemaining}
          isDealer={scenario.villainPosition === 'SB'}
          bet={seatState.villainBet}
          isActive={seatState.active === 'villain'}
          isRaise={seatState.villainBet !== startingBets.villain}
        />

        <div className="blast-hero-seat-wrapper">
          <BlastHeroSeat
            title={scenario.heroPosition}
            bet={seatState.heroBet}
            blinds={heroRemaining}
            card1Value="K"
            card1Suit="d"
            card2Value="Q"
            card2Suit="h"
            isDealer={scenario.heroPosition === 'SB'}
            isActive={seatState.active === 'hero'}
            className={pulseActor === 'hero' ? 'blast-anim-test-pulse' : ''}
            isRaise={seatState.heroBet !== startingBets.hero}
          />
        </div>
      </div>

      {questionText && <QuestionText text={questionText} />}

      {/* Locked action — advances the sequence, does not grade anything */}
      {currentStep?.type === 'lockedAction' && (
        <div className="blast-anim-test-controls">
          <ButtonLarge
            text={currentStep.buttonLabel}
            buttonLargeClassName={`button-large ${currentStep.buttonClassName}`}
            style={{ width: '100%', maxWidth: 343 }}
            onClick={triggerLockedAction}
          />
        </div>
      )}

      {/* Reveal — nothing to tap, just a label while it auto-advances.
          Stays visible for the whole reveal step (both the "their turn"
          hold and the "they acted" beat after) — the chip amount changing
          mid-step is what shows the timing, the label is just context. */}
      {currentStep?.type === 'reveal' && (
        <div className="blast-anim-test-reveal-label">{currentStep.label}</div>
      )}

      {/* Real decision — identical to today's BlastTable buttons */}
      {isDecision && !chosenAnswer && (
        <ButtonGroup
          buttons={currentStep.buttons.map((b) => ({
            text: b.text,
            buttonLargeClassName: b.buttonLargeClassName,
            onClick: () => handleDecision(b.text),
          }))}
        />
      )}

      {isDecision && chosenAnswer && (
        <div className="blast-anim-test-result">
          <p>You answered: {chosenAnswer}</p>
          <ButtonLarge
            text="Replay"
            buttonLargeClassName="button-large blast-raise-button"
            style={{ width: '100%', maxWidth: 343 }}
            onClick={handleReplay}
          />
        </div>
      )}
    </div>
  );
};

export default BlastAnimationTest;
